import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { Alert } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorMessage } from '../../../lib/api';
import { qk } from '../../../lib/queryKeys';
import { campanasApi } from '../../admin/api/campanas.api';
import { metricasApi } from '../../admin/api/metricas.api';
import { Embudo } from '../components/Embudo';
import { TablaCalidad } from '../components/TablaCalidad';
import { useEventosLeads } from '../hooks/useEventosLeads';

// Embudo de ventas + costos de la campaña de Meta Ads (ciclo-metricas-embudo-y-contenido,
// R5, 28/09/2026). Vive dentro de la bandeja (no en /admin) porque es para Ailen, no solo
// para Javier. Se actualiza sola: reusa el mismo stream SSE de la bandeja. Sin título ni
// subtítulo de página (regla de CLAUDE.md §5.8, pedido de Javier el mismo día — el
// primer intento con FunnelChart de recharts quedó feo y este es el rediseño).

const formatoMoneda = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  maximumFractionDigits: 0,
});

export function EmbudoPage(): JSX.Element {
  const es = useI18n();
  const t = es.embudo;
  useEventosLeads(); // se suscribe una vez más acá para que el embudo viva sin la bandeja abierta
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  // D-037, pedido de Javier: el filtro de fecha ocupaba lugar arriba de todo
  // aunque casi nunca se usa -- arranca oculto detrás de un ícono chico.
  const [mostrarFiltroFecha, setMostrarFiltroFecha] = useState(false);

  // D-040 (pedido de Javier, 18/10): elegir la campaña para no mezclar leads de
  // campañas distintas (Clases vs. Taller). Arranca en la más reciente.
  const campanasQuery = useQuery({ queryKey: qk.campanas.all, queryFn: () => campanasApi.list() });
  const campanasMeta = (campanasQuery.data?.campanas ?? [])
    .filter((c) => c.tipo === 'META_ADS')
    .sort((a, b) => b.vigenteDesde.localeCompare(a.vigenteDesde));
  const [campanaElegida, setCampanaElegida] = useState<string | null>(null);
  const campana = campanaElegida ?? campanasMeta[0]?.id;

  const params = {
    ...(desde ? { desde } : {}),
    ...(hasta ? { hasta } : {}),
    ...(campana ? { campana } : {}),
  };
  // Espera a saber las campañas: si no, el primer pedido salía sin filtro y mezclaba.
  const listo = campanasQuery.isFetched;
  const { data, isLoading, error } = useQuery({
    queryKey: qk.metricas.embudo(params),
    queryFn: () => metricasApi.embudo(params),
    enabled: listo,
  });
  // ciclo-calidad-leads (R7): mismo rango de fechas que el embudo.
  const calidadQuery = useQuery({
    queryKey: qk.metricas.calidad(params),
    queryFn: () => metricasApi.calidad(params),
    enabled: listo,
  });

  const etapasEmbudo = data
    ? (['1', '2', '3', '4'] as const).map((e, i) => ({
        n: i + 1,
        label: t.etapas[e],
        // Acumulado (D-034): cuántos leads llegaron ALGUNA VEZ a esta etapa o más
        // lejos — no la foto de dónde están parados ahora (`porEtapa`, sin usar acá).
        count: data.porEtapaAcumulado[e] ?? 0,
      }))
    : [];

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="mb-4 flex items-center justify-between">
        <Link
          to="/bandeja"
          className="inline-flex items-center gap-1 text-sm text-text-subtle hover:text-text-strong"
        >
          <ArrowLeft size={14} />
          {t.volver}
        </Link>
        <div className="flex items-center gap-2">
          {campanasMeta.length > 0 && (
            <select
              value={campana ?? ''}
              onChange={(e) => setCampanaElegida(e.target.value)}
              aria-label={t.campana}
              className="h-8 rounded-md border border-border-strong bg-surface px-2 text-sm text-text-strong"
            >
              {campanasMeta.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={() => setMostrarFiltroFecha((v) => !v)}
            aria-label={t.filtrarPorFecha}
            aria-expanded={mostrarFiltroFecha}
            title={t.filtrarPorFecha}
            className="relative rounded-md p-1.5 text-text-subtle hover:bg-surface-alt hover:text-text-strong"
          >
            <SlidersHorizontal size={16} />
            {(desde || hasta) && (
              <span
                aria-hidden="true"
                className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-marca-primario"
              />
            )}
          </button>
        </div>
      </div>

      {mostrarFiltroFecha && (
        <div className="mb-4 flex flex-wrap items-end gap-3 rounded-md border border-marca-neutro bg-surface p-3">
          <label className="flex flex-col gap-1 text-xs text-text-subtle">
            {t.desde}
            <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="h-9" />
          </label>
          <label className="flex flex-col gap-1 text-xs text-text-subtle">
            {t.hasta}
            <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="h-9" />
          </label>
          {(desde || hasta) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setDesde('');
                setHasta('');
              }}
            >
              {t.limpiarFiltro}
            </Button>
          )}
        </div>
      )}

      {error && (
        <Alert variant="error" className="mt-4">
          {getErrorMessage(error, es.errores.generico.algoSalioMal)}
        </Alert>
      )}
      {isLoading && <p className="mt-6 text-sm text-text-subtle">{t.cargando}</p>}

      {data && (
        <>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TarjetaCosto titulo={t.costoPorMensaje} valor={data.costoPorMensaje} sinDatos={t.sinDatos} />
            <TarjetaCosto
              titulo={t.costoPorCliente}
              valor={data.costoPorClienteCerrado}
              sinDatos={t.sinDatos}
            />
          </div>

          <div className="mt-4">
            <Embudo
              etapas={etapasEmbudo}
              cierre={{
                label: t.etapas[5],
                // Total acumulado que llegó a la etapa 5 (D-035) — no solo `ganado`,
                // que es apenas una de las tres formas en que puede haber terminado.
                count: data.porEtapaAcumulado['5'] ?? 0,
                desglose: data.cierreDesglose,
              }}
            />
          </div>

          <div className="mt-4 rounded-md border border-marca-neutro bg-surface p-4 shadow-card">
            <div className="mb-1 flex items-center justify-between">
              <p className="text-xs font-medium uppercase tracking-wide text-text-subtle">{t.gastoPorDia}</p>
              <p className="text-xs font-semibold text-text-marca">
                {t.gastoTotal.replace('{monto}', formatoMoneda.format(data.gastoTotal))}
              </p>
            </div>
            {data.gastoPorDia.length === 0 ? (
              <p className="py-6 text-center text-sm text-text-faint">{t.sinDatos}</p>
            ) : (
              <div className="h-[220px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.gastoPorDia} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="hsl(var(--border-subtle))"
                      vertical={false}
                    />
                    <XAxis
                      dataKey="fecha"
                      tick={{ fontSize: 10 }}
                      tickFormatter={(v: string) => v.slice(5)}
                    />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip formatter={(v) => formatoMoneda.format(Number(v))} />
                    <Bar dataKey="spend" fill="hsl(var(--marca-primario))" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      )}

      {calidadQuery.data && (
        <>
          <TablaCalidad
            etiqueta={t.calidad.porAnuncio}
            columnaNombre={t.calidad.anuncio}
            filas={calidadQuery.data.porAnuncio}
            total={calidadQuery.data.total}
          />
          <TablaCalidad
            etiqueta={t.calidad.porCampana}
            columnaNombre={t.calidad.campana}
            filas={calidadQuery.data.porCampana}
          />
        </>
      )}
    </div>
  );
}

function TarjetaCosto({
  titulo,
  valor,
  sinDatos,
}: {
  titulo: string;
  valor: number | null;
  sinDatos: string;
}): JSX.Element {
  return (
    <div className="rounded-md border border-marca-neutro bg-surface p-5 shadow-card transition-shadow hover:shadow-lg">
      <p className="text-xs font-medium uppercase tracking-wide text-text-subtle">{titulo}</p>
      {valor === null ? (
        <p className="mt-2 text-sm text-text-faint">{sinDatos}</p>
      ) : (
        <p className="mt-1 text-4xl font-bold tabular-nums text-text-marca">{formatoMoneda.format(valor)}</p>
      )}
    </div>
  );
}
